import type { GetStaticPaths, GetStaticProps } from 'next';
import type { SingleConditionProps } from 'lib/queries/conditions';
import React, { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { Card, Paragraph, Wrap, Heading } from '@leafwell/components';
import Loader from 'components/loader';
import PageWrap from 'components/page-wrap';
import HeadingBody from 'components/heading-body';
import WysiwygContent from 'components/wysiwyg-content';
import ConditionsByLetter from 'components/conditions-by-letter';
import { EfficiencyIcon } from 'components/efficiency-icon';
import MedicalCardHighlight from 'components/medical-card-highlight';
import StudyLegend from 'components/study-legend';
import Line from 'components/line';
import Navbar from 'components/navbar';
import SimpleCardCta from 'components/simple-card-cta';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { getSingleCondition } from 'lib/queries/conditions';
import { getLargestRemainderPercentages } from 'lib/utils/getLargestRemainderPercentages';

const CONDITION_SECTIONS = [
  'overview',
  'research-overview',
  'do-i-qualify',
  'relevant-studies',
];

export type ConditionSections = { label: string; id: string }[];

const OverviewSection: React.FC<{
  id: string;
  conditionTitle: string;
  content: string;
}> = ({ id, conditionTitle, content }) => {
  const { translations } = useWordPressContext();
  return (
    <section
      key={id}
      id={id}
      className="grid md:grid-cols-condition-details gap-x-24 mb-10"
    >
      <Heading level="2" className="font-sans flex-1 mb-8 md:mb-0">
        {`${translations['Cannabis for']} ${conditionTitle}`}
      </Heading>
      <WysiwygContent content={content} />
    </section>
  );
};

const ResearchOverviewSection: React.FC<
  SingleConditionProps['conditionStudies'] & {
    id?: string;
    conditionTitle?: string;
  }
> = ({ id, studies, resultsDetails, typesDetails, conditionTitle }) => {
  const { translations } = useWordPressContext();

  const totalStudies = studies.pagination.aggregate.count;

  const percentages = useMemo(
    () =>
      getLargestRemainderPercentages(resultsDetails.map(({ count }) => count)),
    [resultsDetails],
  );
  const getPercentage = useCallback(
    key => percentages.find(({ key: itemKey }) => key === itemKey)?.value || 0,
    [percentages],
  );

  const STUDY_TYPES_OVERVIEW = useMemo(
    () => [
      ...typesDetails,
      {
        type: translations['Total studies'],
        count: totalStudies,
      },
    ],
    [typesDetails, totalStudies],
  );

  return (
    <section key={id} id={id}>
      <Line className="mt-24 mb-9" />
      <div className="grid md:grid-cols-4 gap-x-10 md:gap-y-8 items-end mb-8 md:mb-24">
        {STUDY_TYPES_OVERVIEW.map(({ type, count }, idx) => {
          const isLast = idx === STUDY_TYPES_OVERVIEW.length - 1;

          return (
            <div
              key={idx}
              className={[
                'flex-col mb-16 md:mb-0',
                isLast ? 'md:col-span-2' : '',
              ].join(' ')}
            >
              <Card
                size="medium"
                filled={true}
                rounded={true}
                variant="light"
                tagName="article"
                className="relative h-48"
              >
                {type && <Paragraph>{translations[type] || type}</Paragraph>}
                {isLast ? (
                  <Heading
                    level="4"
                    className="font-sans absolute top-12 left-6"
                  >
                    {conditionTitle}
                  </Heading>
                ) : null}
                <Heading level="3" className="self-end">
                  {count}
                </Heading>
              </Card>
            </div>
          );
        })}
      </div>
      <section>
        {resultsDetails.map(({ count, result }, idx) => {
          const percentage = getPercentage(idx);

          return (
            <div key={idx} className="grid grid-cols-3 mb-12">
              <Heading level="4" className="font-sans">
                {result}
              </Heading>
              <Paragraph
                size="small"
                className="text-center"
              >{`${count} ${translations['studies']}`}</Paragraph>
              <Heading level="3" className="text-right">
                {`${percentage}%`}
              </Heading>
              <div
                className="col-span-full border-t-[3px] border-green"
                style={{
                  width: percentage === 0 ? '0.5%' : `${percentage}%`,
                }}
              ></div>
            </div>
          );
        })}
      </section>
    </section>
  );
};

const DoIQualifySection: React.FC<
  Pick<SingleConditionProps['post']['acf'], 'cta'> & {
    id?: string;
  }
> = ({ cta, id }) => {
  return (
    <section key={id} id={id}>
      <MedicalCardHighlight {...cta} wrap={false} />
    </section>
  );
};

const RelevantStudies: React.FC<
  Pick<SingleConditionProps['conditionStudies'], 'studies'> & {
    id?: string;
    studiesTitle?: string;
    studiesDescription?: string;
  }
> = ({ studies, id, studiesTitle, studiesDescription }) => (
  <section
    id={id}
    className="mt-8 md:mt-28 grid md:grid-cols-[40%_auto] lg:grid-cols-condition-details md:gap-x-16 lg:gap-x-40"
  >
    <div className="mb-16">
      <HeadingBody className="!mb-8" level="3" title={studiesTitle} />
      <Paragraph dangerouslySetInnerHTML={{ __html: studiesDescription }} />
    </div>
    <ul>
      {studies.nodes.map(({ title, link, typeofstudy, result }, idx) => (
        <li
          key={`study-${idx}`}
          className="mb-16 md:mb-4 rounded-lg md:p-8 hover:bg-lilac-50"
        >
          <a
            className="underline underline-offset-2 hover:cursor-pointer"
            href={link}
            target="_blank"
          >
            {title}
            <Image
              className="inline-block ml-2"
              src="/external-link-icon.svg"
              height={15}
              width={15}
              alt=""
            />
          </a>
          <StudyLegend
            type={typeofstudy}
            result={result}
            className="mt-5 flex-col lg:flex-row"
          />
        </li>
      ))}
    </ul>
  </section>
);

const ConditionContent: React.FC<
  Pick<SingleConditionProps['post'], 'title' | 'content'> &
    Pick<
      SingleConditionProps['post']['acf'],
      'cta' | 'studiesTitle' | 'studiesDescription'
    > &
    SingleConditionProps['conditionStudies'] & {
      sections: ConditionSections;
    }
> = ({
  title: conditionTitle,
  content,
  sections,
  cta,
  studiesTitle,
  studiesDescription,
  studies,
  resultsDetails,
  typesDetails,
}) => {
  return (
    <Wrap className="!pt-0">
      {sections.map(({ id }) => {
        switch (id) {
          case CONDITION_SECTIONS[0]:
            return (
              <OverviewSection
                key={id}
                id={id}
                conditionTitle={conditionTitle}
                content={content}
              />
            );
          case CONDITION_SECTIONS[1]:
            return (
              <ResearchOverviewSection
                key={id}
                id={id}
                studies={studies}
                resultsDetails={resultsDetails}
                typesDetails={typesDetails}
                conditionTitle={conditionTitle}
              />
            );
          case CONDITION_SECTIONS[2]:
            return <DoIQualifySection key={id} id={id} cta={cta} />;
          case CONDITION_SECTIONS[3]:
            return (
              <RelevantStudies
                key={id}
                id={id}
                studies={studies}
                studiesTitle={studiesTitle}
                studiesDescription={studiesDescription}
              />
            );
        }
      })}
    </Wrap>
  );
};

const SinglePostContent: React.FC<SingleConditionProps> = ({
  post: {
    title,
    content,
    seo,
    acf: { efficiency, cta, studiesTitle, studiesDescription },
  },
  conditionsByLetter: { conditions, pagination, letter },
  conditionStudies: { studies, resultsDetails, typesDetails },
}) => {
  const { translations, conditionsTopCtaImage } = useWordPressContext();

  const sections = useMemo(() => {
    const filteredSections = [];
    CONDITION_SECTIONS.map(id => {
      switch (id) {
        case 'overview':
          if (content) {
            filteredSections.push({
              id,
              label: translations[id],
            });
          }
          break;
        case 'research-overview':
        case 'relevant-studies':
          if (studies?.nodes?.length > 0) {
            filteredSections.push({
              id,
              label: translations[id],
            });
          }
          break;
        default:
          filteredSections.push({
            id,
            label: translations[id],
          });
      }
    });
    return filteredSections;
  }, [translations, content, studies]);

  return (
    <PageWrap seo={seo} title={title} pageType={null}>
      <header className="relative before:-z-1 before:pointer-events-none before:bg-gray-100 before:absolute before:bottom-0 before:left-0 before:w-screen before:h-screen">
        <ConditionsByLetter
          letter={letter}
          variant="minimal"
          activeCondition={title}
          conditions={conditions}
          pagination={pagination}
        />
      </header>
      <SimpleCardCta
        layout="secondary"
        label={translations['Medical Card']}
        title={translations['Get Your Medical Marijuana Card Today']}
        description={
          translations[
            'Meet with a healthcare provider in minutes and receive the best care for the best value.'
          ]
        }
        image={conditionsTopCtaImage}
        button={null}
        stateSelect={true}
      />
      <Wrap>
        <div className="flex md:items-center flex-col md:flex-row">
          <HeadingBody
            title={title}
            level="2"
            tagName="h1"
            className="md:w-2/3 md:pr-4"
          />
          {efficiency && (
            <div className="flex md:justify-end md:pt-0 pt-8 md:w-1/2">
              <EfficiencyIcon />
              <HeadingBody className="pl-4" title={efficiency} level="4" />
            </div>
          )}
        </div>
        <Navbar
          classNameWrap="px-reset"
          items={sections.map(({ id, label }) => ({
            name: label,
            uri: `#${id}`,
          }))}
          variant="border"
        />
      </Wrap>
      <ConditionContent
        title={title}
        sections={sections}
        content={content}
        studies={studies}
        cta={cta}
        studiesTitle={studiesTitle}
        studiesDescription={studiesDescription}
        resultsDetails={resultsDetails}
        typesDetails={typesDetails}
      />
    </PageWrap>
  );
};

const SingleCondition: React.FC<SingleConditionProps> = props => {
  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  return <SinglePostContent {...props} />;
};

export const getStaticProps: GetStaticProps = async context => {
  return await getSingleCondition(context);
};

export const getStaticPaths: GetStaticPaths = async context => {
  return { paths: [], fallback: 'blocking' };
};

export default SingleCondition;
